import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faEnvelope, 
  faPhone, 
  faMapMarkerAlt, 
  faPaperPlane, 
  faSpinner,
  faCheckCircle,
  faUser,
  faEnvelopeOpen,
  faTag,
  faCommentDots
} from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import Footer from '../components/Footer';
import './Contact.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const servicesList = [
  "Web Development",
  "Mobile App Development",
  "Payment Integration",
  "Database Optimization",
  "Website Designing",
  "Software Testing and Consultancy",
  "Penetration Testing",
  "Graphic Designing",
  "General Inquiry"
];

const Contact = () => {
  const location = useLocation();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  // Automatically pre-fill the selected service if redirected from Navbar or Services page
  useEffect(() => {
    if (location.state && location.state.selectedService) {
      setFormData(prev => ({
        ...prev,
        subject: location.state.selectedService
      }));
    }
  }, [location.state]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMsg('');

    try {
      await axios.post(`${API_BASE_URL}/contacts`, formData);
      setSubmitted(true);
      setFormData({ name: '', email: '', subject: '', message: '' });
      setTimeout(() => setSubmitted(false), 6000);
    } catch (error) {
      console.error('Failed to submit contact message:', error);
      setErrorMsg(error.response?.data?.message || 'Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="contact-page-wrapper">
      <div className="contact-page">
        {/* Header Section */}
        <header className="contact-header">
          <h1>Get In Touch With <span>Stemiot</span></h1>
          <p>Have a project in mind, an inquiry or need specialized engineering advice? We are ready to help you build and scale.</p>
        </header>

        <div className="contact-wrapper">
          {/* Information Sidebar */}
          <aside className="contact-info-panel">
            <div className="info-panel-header">
              <h3>Contact Information</h3>
              <p>Reach out directly or send us a message through the form.</p>
            </div>

            <div className="info-list">
              <a href="mailto:stemiotsoftwares@gmail.com" className="info-item">
                <div className="info-icon">
                  <FontAwesomeIcon icon={faEnvelope} />
                </div>
                <div className="info-text">
                  <span className="info-label">Email Us</span>
                  <p>stemiotsoftwares@gmail.com</p>
                </div>
              </a>

              <a href="tel:+254115598800" className="info-item">
                <div className="info-icon">
                  <FontAwesomeIcon icon={faPhone} />
                </div>
                <div className="info-text">
                  <span className="info-label">Call Directly</span>
                  <p>+254 115 598 800</p>
                </div>
              </a>

              <div 
                className="info-item whatsapp-item" 
                onClick={() => window.open('https://wa.me/254115598800', '_blank')}
              >
                <div className="info-icon whatsapp-icon">
                  <FontAwesomeIcon icon={faWhatsapp} />
                </div>
                <div className="info-text">
                  <span className="info-label">Instant Chat</span>
                  <p>Chat on WhatsApp</p>
                </div>
              </div>

              <div className="info-item">
                <div className="info-icon">
                  <FontAwesomeIcon icon={faMapMarkerAlt} />
                </div>
                <div className="info-text">
                  <span className="info-label">Headquarters</span>
                  <p>Nairobi, Kenya</p>
                </div>
              </div>
            </div>

            
          </aside>

          {/* Form Container */}
          <section className="contact-form-panel">
            {submitted ? (
              <div className="success-banner">
                <FontAwesomeIcon icon={faCheckCircle} className="success-icon" />
                <h3>Thank You! Message Received</h3>
                <p>Our engineering lead will review your requirements and reach out within 24 hours.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="contact-form">
                {errorMsg && (
                  <div className="error-banner">
                    {errorMsg}
                  </div>
                )}

                {/* 2-Column Split for Name and Email to prevent overstretching */}
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">
                      <FontAwesomeIcon icon={faUser} className="field-icon" /> Full Name
                    </label>
                    <input 
                      id="name"
                      type="text" 
                      name="name" 
                      value={formData.name}
                      placeholder="e.g. John Kamau" 
                      required 
                      onChange={handleChange} 
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="email">
                      <FontAwesomeIcon icon={faEnvelopeOpen} className="field-icon" /> Email
                    </label>
                    <input 
                      id="email"
                      type="email" 
                      name="email" 
                      value={formData.email}
                      placeholder="e.g. johnk@gmail.com" 
                      required 
                      onChange={handleChange} 
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="subject">
                    <FontAwesomeIcon icon={faTag} className="field-icon" /> Service of Interest
                  </label>
                  <select 
                    id="subject"
                    name="subject" 
                    value={formData.subject} 
                    required 
                    onChange={handleChange}
                  >
                    <option value="">Select a technical solution or service...</option>
                    {servicesList.map((service, idx) => (
                      <option key={idx} value={service}>
                        {service}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="message">
                    <FontAwesomeIcon icon={faCommentDots} className="field-icon" /> Project Description
                  </label>
                  <textarea 
                    id="message"
                    name="message" 
                    value={formData.message}
                    placeholder="Tell us about your project goals, timelines or specifications..." 
                    rows="5" 
                    required 
                    onChange={handleChange}
                  ></textarea>
                </div>

                <button type="submit" className="btn-contact-submit" disabled={loading}>
                  {loading ? (
                    <>Sending Message... <FontAwesomeIcon icon={faSpinner} spin /></>
                  ) : (
                    <>Send Inquiry <FontAwesomeIcon icon={faPaperPlane} /></>
                  )}
                </button>
              </form>
            )}
          </section>
        </div>
      </div>

      {/* <Footer /> */}
    </div>
  );
};

export default Contact;